import { useCallback, useEffect, useState } from 'react'
import { usePagination } from 'hooks/pagination.hook'
import client from '../../../services/client'
import { BaseDataContent } from 'types/base.type'
import { useSnackBar } from 'contexts/snackbar.context'
import { HttpErrorType } from 'types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'
import { useBackdrop } from 'contexts/backdrop.context'
import { useNavigate } from 'react-router-dom'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { PromotoriaType } from 'hooks/promotoria.hook'

export type NotificationType = {
  id: number
  titulo: string
  dataRegistro: string
  processo: {
    id: number
    numeroCnj: string
  }
  visualizado: boolean
}

const FIRST_PAGE_NUMBER = 0

export const useNotifications = (promotoria?: PromotoriaType) => {
  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()
  const {
    page,
    rows: { rowsPerPage, rowsOptions },
    pages: { handleTotalOfPages, totalOfPages },
    elements: { totalOfElements, handleTotalOfElements },
    handleNewPage,
    handleRowsPerPage,
  } = usePagination()

  const [notifications, setNotifications] = useState<NotificationType[]>([])
  const [loaded, setLoaded] = useState(false)
  const [usuarioPossuiNotificacao, setUsuarioPossuiNotificacao] = useState<boolean>(false)

  let currentController: AbortController | null = null // Variável de controle externa

  const buscarUsuarioPossuiNotificacao = async () => {
    const url = `/notificacao/usuario`
    client
      .get(url)
      .then(() => setUsuarioPossuiNotificacao(true))
      .catch(() => setUsuarioPossuiNotificacao(false))
  }

  const handleBuscarNotificacoes = useCallback(
    async (promotoria?: PromotoriaType, resetPage = false) => {
      if (currentController) {
        // Evita chamadas simultâneas ou cancela a anterior
        currentController.abort()
      }

      const controller = new AbortController()
      currentController = controller

      const newPage = resetPage ? FIRST_PAGE_NUMBER : page // Define a página a ser utilizada

      if (resetPage) {
        handleNewPage(null, FIRST_PAGE_NUMBER) // Reseta a página no estado global
      }

      const url = `/notificacao`
      if (promotoria) {
        handleBackdropOpen()
        await client
          .post(url)
          .then(async () => {
            await client
              .get<BaseDataContent<NotificationType[]>>(url, {
                promotoriaId: promotoria.id,
                page: newPage,
                pageSize: rowsPerPage,
              })
              .then((response) => {
                if (response.data && response.data.content) {
                  handleTotalOfPages(response.data.totalPages)
                  handleTotalOfElements(response.data.totalElements)
                  setNotifications(response.data.content)
                }
              })
              .catch((error: HttpErrorType) => {
                showSnackBar(errorMessage(error), errorColor(error))
              })
          })
          .catch((error: HttpErrorType) => {
            showSnackBar(errorMessage(error), errorColor(error))
          })
          .finally(() => {
            setLoaded(true)
            handleBackdropClose()
            if (currentController === controller) {
              currentController = null // Reseta o controlador
            }
          })
      } else {
        setLoaded(true)
      }
    },
    [page, rowsPerPage],
  )

  const handleVisualizarNotificacao = async (notificacao?: NotificationType | null) => {
    if (notificacao) {
      const url = `/notificacao/${notificacao?.id}`
      handleBackdropOpen()
      await client
        .put<BaseDataContent<NotificationType[]>>(url)
        .then(() => navigate(`/processos-pendentes/${notificacao.processo.id}`))
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    }
  }

  const handleExcluirLidos = async () => {
    const url = `/notificacao/lidos`
    if (promotoria) {
      handleBackdropOpen()
      await client
        .delete(url, {
          promotoriaId: promotoria.id,
        })
        .then(() => showSnackBar(SystemMessages.MESSAGE_024, 'success'))
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
      await handleBuscarNotificacoes()
      handleBackdropClose()
    }
  }

  const handleExcluirSelecionados = async (notificacoes?: NotificationType[]) => {
    const url = `/notificacao`
    if (notificacoes) {
      handleBackdropOpen()
      await client
        .delete(url, notificacoes)
        .then(() => showSnackBar(SystemMessages.MESSAGE_024, 'success'))
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
      await handleBuscarNotificacoes()
      handleBackdropClose()
    }
  }

  useEffect(() => {
    ;(async () => {
      await handleBuscarNotificacoes(promotoria)
    })()
  }, [handleBuscarNotificacoes])

  useEffect(() => {
    ;(async () => {
      await buscarUsuarioPossuiNotificacao()
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      await handleBuscarNotificacoes(promotoria, true)
    })()
  }, [promotoria])

  return {
    notifications,
    loaded,
    usuarioPossuiNotificacao,

    page,
    rowsOptions,
    rowsPerPage,
    totalOfPages,
    totalOfElements,
    handleNewPage,
    handleRowsPerPage,

    handleVisualizarNotificacao,
    handleExcluirSelecionados,
    handleExcluirLidos,
  }
}
